import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { DrawerProps, Radio as AntRadio } from 'antd';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { generateOptions } from '@utils/generateOptions';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Radio } from '@dynamika-shared/ui/Radio/Radio';
import { useForm } from 'react-hook-form';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { SearchIcon } from '@dynamika-shared/ui/icons';
import { NoDataSelect } from '@dynamika-shared/ui/NoDataSelect/NoDataSelect';
import {
  CustomOptionType,
  OptionRenderer,
} from '@components/OptionRenderer/OptionRenderer';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { drawersProps } from 'src/pages/ClinicalTrial/ClinicalTrialForms/drawers/index';
import { useQuestionnairesSlice } from '@restore/questionnaires/questionnaires.slice';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';
import { useParams } from '@hooks/useParams';
import { TemplateContext } from 'src/pages/ClinicalTrial/ClinicalTrialForms/ClinicalTrialForms';
import { AssignTemplateFormType } from '@restore/questionnaires/questionnaires.types';
import { omit } from 'lodash';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { getInputDataTypes } from 'src/pages/ClinicalTrial/ClinicalTrialForms/drawers/getDataTypesFromLabels';
import { VisitConfig } from '@dynamika-shared/types/visits';

type AssignFormDrawerProps = DrawerProps & {
  close?: () => void;
};

type FieldName = keyof AssignTemplateFormType;

export const AssignFormDrawer: FC<AssignFormDrawerProps> = ({ close }) => {
  const context = useContext(TemplateContext);
  const { id } = useParams();

  const labelGroups = useClinicalTrialSlice(
    state => state.data?.labelGroups || []
  );

  const [questionnaires, assignForm] = useQuestionnairesSlice(
    (state, actions) => [
      state.clinicalTrialQuestionnaires,
      actions.assignClinicalTrialQuestionnaireAction,
    ]
  );

  const [searchValue, setSearchValue] = useState<string>('');
  const [askQuit, setAskQuit] = useState<boolean>(false);

  const { watch, setValue, formState, trigger, getValues } =
    useForm<AssignTemplateFormType>({
      resolver: yupResolver(
        yup.object().shape({
          visitDefinitionId: yup.string().required(),
          clinicalTrialQuestionnaireId: yup.string().required(),
          dataType: yup
            .mixed<UploadFileTypeEnum>()
            .oneOf(Object.values(UploadFileTypeEnum))
            .required(),
        })
      ),
    });
  const { isValid, isDirty, touchedFields } = formState;

  const dataTypes = useMemo(
    () => getInputDataTypes(labelGroups, context?.currentVisit as VisitConfig),
    [labelGroups, context?.currentVisit]
  );

  const questionnairesOptions = useMemo(
    () =>
      generateOptions(
        questionnaires.map(questionnaire => ({
          id: questionnaire.id,
          name: questionnaire.name,
        })),
        'id',
        'name'
      ),
    [questionnaires, context?.currentVisit]
  );

  const renderOption = useCallback(
    (option: CustomOptionType) => {
      const selected = questionnairesOptions.find(
        el => el.value === watch('clinicalTrialQuestionnaireId')
      );
      return (
        <OptionRenderer
          searchValue={searchValue}
          option={option}
          selectedOptions={selected ? [selected] : []}
          select={'Select'}
          selected={'Selected'}
        />
      );
    },
    [searchValue, questionnairesOptions]
  );

  const onCloseDrawer = useCallback(() => {
    const shouldAskQuit = isDirty || Object.keys(touchedFields).length > 0;
    shouldAskQuit ? setAskQuit(true) : close?.();
  }, [touchedFields, close]);

  const onChange = (name: FieldName, value: string) => {
    setValue(name, value, { shouldDirty: true, shouldTouch: true });
    trigger(name);
  };

  const handleAssignTemplate = () => {
    if (id) {
      const data =
        getValues().visitDefinitionId === 'default'
          ? omit(getValues(), 'visitDefinitionId')
          : getValues();

      assignForm({ id, data });
      close?.();
    }
  };

  useEffect(() => {
    context?.currentVisit?.id &&
      setValue('visitDefinitionId', context?.currentVisit?.id);
  }, [context?.currentVisit?.id]);

  return (
    <Drawer
      {...drawersProps}
      data-testid='template-form-drawer'
      title={
        <span className={'text-spanishGray'}>
          Assign Form to{' '}
          <span className={'text-black'}>{context?.currentVisit?.name}</span>
        </span>
      }
      placement='right'
      onClose={onCloseDrawer}
    >
      <div className='px-1'>
        <Select
          className='mb-2'
          label={'Select Template'}
          menuItemSelectedIcon={null}
          suffixIcon={<SearchIcon className='h-2 w-2' />}
          required
          options={questionnairesOptions}
          placeholder={'Search Template'}
          filterOption={(input, option) =>
            (option?.label as string)
              ?.toLowerCase()
              ?.includes(input.toLowerCase())
          }
          searchValue={searchValue}
          notFoundContent={
            searchValue ? (
              <NoDataSelect
                text={searchValue}
                maxLength={10}
                name={'template'}
              />
            ) : null
          }
          onSearch={value => setSearchValue(value)}
          value={watch('clinicalTrialQuestionnaireId')}
          showSearch
          listHeight={160}
          onBlur={() => setSearchValue('')}
          onSelect={value => onChange('clinicalTrialQuestionnaireId', value)}
          optionRender={renderOption}
          name='questionnaires'
          data-testid='template-questionnaires'
        />
        <div className={'text-1424 font-semibold mb-1'}>Apply to*</div>
        <AntRadio.Group
          className={'flex flex-col gap-[12px]'}
          onChange={e => onChange('dataType', e.target.value)}
          value={watch('dataType')}
        >
          {dataTypes.map(dataType => (
            <Radio color={'dodgerBlue'} key={dataType} value={dataType}>
              {dataType}
            </Radio>
          ))}
        </AntRadio.Group>
      </div>
      <footer className='mt-auto pr-2'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button className='w-[17rem]' onClick={onCloseDrawer}>
            Cancel
          </Button>
          <Button
            data-testid='template-save-btn'
            className='w-[17rem]'
            type='primary'
            disabled={!isValid}
            onClick={handleAssignTemplate}
          >
            Add
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        onConfirm={() => close && close()}
        onCancel={() => setAskQuit(false)}
        open={askQuit}
      />
    </Drawer>
  );
};
